import type { Database } from '~/apis/generated/database'
import { useApi } from '~/composables/useApi'
import { useSupabaseClient } from '~/composables/useSupabaseClient'
import type { Location } from '~/entities/location'
import type { SortOptions } from '~/entities/supabase'
import type { User, UserProfile, UserProfileUpdateParams } from '~/entities/user'
import { toEdgeFunctionFailure, type EdgeFunctionResult } from '~/utils/edgeFunction'

export const getUser = async (id: number) => {
  const { data, error } = await useSupabaseClient<Database>()
    .from('users')
    .select('*, user_email_settings(*)')
    .eq('id', id)
    .single()
  if (error) {
    throw error
  }
  return data
}

export const getUserByAccountId = async (accountId: string) => {
  const { data, error } = await useSupabaseClient<Database>()
    .from('users')
    .select('*, user_accounts!user_id!inner(id, is_pending), user_email_settings(*)')
    .eq('user_accounts.id', accountId)
    .single()
  if (error) throw error
  return data
}

const UPDATE_USERS_ERROR_CODES = [
  'unauthorized',
  'targetUserNotFound',
  'permissionDenied',
  'userBelongsToMultipleLocations',
  'duplicateEmail',
  'userLimitExceeded',
  'locationLimitExceeded',
  'userInfoNotFound',
  'unknown',
] as const

export type UpdateUsersErrorCode = (typeof UPDATE_USERS_ERROR_CODES)[number]

export const updateUser = async (
  userId: number,
  params: UserProfileUpdateParams & {
    password?: string
  }
): Promise<EdgeFunctionResult<UserProfile, UpdateUsersErrorCode>> => {
  const supabase = useSupabaseClient<Database>()

  const { data, error } = await supabase.functions.invoke('update-user', {
    body: {
      // user
      userId,
      name: params.name,
      name_pronunciation: params.name_pronunciation,
      role: params.role,
      locationIds: params.locationIds,
      // userAccount
      email: params.email,
      password: params.password,
      is_pending: params.is_pending,
      // emailSetting
      enabled_my_page_notifications: params.enabled_my_page_notifications,
      enabled_assigned_ticket_notifications: params.enabled_assigned_ticket_notifications,
      enabled_daily_verifier_notifications: params.enabled_daily_verifier_notifications,
    },
  })
  if (error) {
    return toEdgeFunctionFailure(error, UPDATE_USERS_ERROR_CODES)
  }

  return { success: true, data: data as UserProfile }
}

export const getUsers = async (
  params: {
    userId?: number
    accountId?: string
    locationIds?: number[]
    roles?: User['role'][]
    emails?: string[]
  } = {},
  sortOptions: SortOptions<User> = [
    { column: 'role', order: 'asc' },
    { column: 'name', order: 'asc' },
    { column: 'id', order: 'asc' },
  ]
) => {
  const { userId, accountId, locationIds, roles, emails } = params
  const query = useSupabaseClient()
    .from('users')
    .select(`*, user_accounts!inner(*), user_email_settings(*), locations!user_locations(*)`)
  if (userId) {
    query.eq('id', userId)
  }
  if (accountId) {
    query.eq('user_accounts.id', accountId)
  }
  if (roles) {
    query.in('role', roles)
  }
  if (emails) {
    query.in('user_accounts.email', emails)
  }
  for (const { column, order } of sortOptions) {
    query.order(column, { ascending: order === 'asc' })
  }
  query.order('position', { referencedTable: 'locations', ascending: true })
  query.order('id', { referencedTable: 'locations', ascending: true })
  const { data, error } = await query
  if (error) throw error
  return data
    .filter((u) => {
      if (!locationIds) return true
      // 紐づく locations はすべて取得したいため、絞り込みは filter で行う
      return u.locations.some((l) => locationIds.includes(l.id))
    })
    .map((d) => ({ ...d, user_account: d.user_accounts[0] })) // inner しているので user_accounts は必ず1件あるはずだが、型定義上は配列になっているため user_account として1件目を返す
}

export const getAdminsCount = async (role: User['role'], locationId: Location['id'] | null) => {
  const query = useSupabaseClient()
    .from('users')
    .select(`id, user_accounts!inner(id), user_locations${locationId ? '!inner' : ''}(user_id)`, {
      count: 'exact',
      head: true,
    })
    .eq('role', role)
  if (role !== 'ORGANIZATION_ADMIN' && locationId) {
    query.eq('user_locations.location_id', locationId)
  }
  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}

export async function getUsersCounts(role: User['role'], locationIds: number[]): Promise<number[]> {
  if (role === 'ORGANIZATION_ADMIN') {
    const { count, error } = await useSupabaseClient()
      .from('users')
      .select('id, user_accounts!inner(id)', { count: 'exact', head: true })
      .eq('role', role)
    if (error) {
      throw error
    }
    return [count ?? 0]
  }

  const { data, error } = await useSupabaseClient()
    .from('locations')
    .select('id, users!user_locations(id)')
    .in('id', locationIds)
    .eq('users.role', role)
    .order('id', { ascending: true })
  if (error) {
    throw error
  }
  return data.map((location) => location.users.length)
}

// 自分自身・権限のないユーザーを含む場合は FORBIDDEN になる。
// 自分自身は呼び出し側で送信前に除外すること（API のエラーからは理由を出し分けない）
export const deleteUsers = async (userIds: number[]): Promise<void> => {
  const { client } = useApi()
  const response = await client.users.$delete({
    json: { userIds },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Failed to delete users: ${response.status} ${response.statusText}: ${body}`)
  }
}
